/**
 * Tabs Atom — compound (controlled) API.
 *
 * Use this when you need per-panel conditional rendering, lazy mounting,
 * custom panel layout, or route-synced active-tab state. For simple
 * inline tabs whose panels are all already-rendered React nodes, prefer
 * the flat-config `Tabs` exported from `@plerity/dashboard-shared/components`.
 *
 * Boundary rationale: `agents/analysis/tabs_api_dual_residency_decision_2026-05-24.md`.
 *
 * @layer atom
 * @package @plerity/ui-core
 */

import { clsx } from "clsx";
import { createContext, useContext, useId } from "react";
import type { TabListProps, TabPanelProps, TabProps, TabsProps, TabsVariant } from "./Tabs.types";

interface TabsContextValue {
  value: string;
  onValueChange: (value: string) => void;
  variant: TabsVariant;
  /** Shared ID prefix so Tab `aria-controls` and TabPanel `id` are always in sync. */
  tabsId: string;
}

const TabsContext = createContext<TabsContextValue | null>(null);

function useTabsContext() {
  const ctx = useContext(TabsContext);
  if (!ctx) throw new Error("Tab components must be used within <Tabs>");
  return ctx;
}

/** Sanitize an arbitrary `value` string for safe use inside an HTML id attribute. */
function toIdSegment(v: string): string {
  return v.replace(/[^a-zA-Z0-9_-]/g, "-");
}

export function Tabs({ variant = "pill", value, onValueChange, className, children, ...props }: TabsProps) {
  const tabsId = useId();
  return (
    <TabsContext.Provider value={{ value, onValueChange, variant, tabsId }}>
      <div className={className} {...props}>
        {children}
      </div>
    </TabsContext.Provider>
  );
}

export function TabList({ className, children, ...props }: TabListProps) {
  const { variant } = useTabsContext();

  return (
    <div
      role="tablist"
      // `overflow-x-auto` ensures wide tab lists (>6 tabs at mobile widths)
      // scroll within the tablist instead of overflowing the flex parent. The
      // 2026-04-27 mobile-sidebar overlap incident (commit e4d9018166)
      // documents the failure mode: without intra-tablist scroll, the browser
      // auto-scrolls the parent <main> when a focused tab needs to be brought
      // into view, which can shift page content behind a fixed/flex sidebar.
      className={clsx(
        "flex overflow-x-auto",
        // Literal gray ramp — do NOT use `bg-surface-subtle` or
        // `border-[var(--color-border-default)]` here. Those emit `var(--color-*)`
        // with NO fallback and are stripped from the React SPA apps' Tailwind v4 build.
        variant === "pill" && "bg-gray-100 rounded-md p-1 gap-1 dark:bg-gray-700",
        variant === "underline" && "border-b-2 border-gray-200 gap-0 dark:border-gray-700",
        className
      )}
      {...props}
    >
      {children}
    </div>
  );
}

export function Tab({ value: tabValue, disabled = false, className, children, ...props }: TabProps) {
  const { value, onValueChange, variant, tabsId } = useTabsContext();
  const isActive = value === tabValue;
  const seg = toIdSegment(tabValue);

  return (
    <button
      id={`tab-${tabsId}-${seg}`}
      role="tab"
      aria-selected={isActive}
      aria-controls={`tabpanel-${tabsId}-${seg}`}
      disabled={disabled}
      onClick={() => !disabled && onValueChange(tabValue)}
      className={clsx(
        "px-4 py-1.5 text-sm whitespace-nowrap",
        "transition-all duration-150",
        // Suppress default outline but restore focus ring for keyboard users (WCAG 2.4.7).
        // Uses the shared `ring-brand-focus` token (same as Button/Input/Select) — no
        // hardcoded hex fallback, which would otherwise leak a literal `#3b82f6`.
        "focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-focus focus-visible:ring-offset-1",
        disabled && "opacity-50 cursor-not-allowed",
        variant === "pill" && [
          "rounded",
          // Active pill: literal white/gray-800 surface — do NOT use `bg-surface` or
          // `border-[var(--color-border-default)]` / `text-[var(--color-text-primary)]`.
          // Those emit `var(--color-*)` with NO fallback, stripped from the SPA build.
          isActive
            ? "bg-white border border-gray-200 shadow-sm text-gray-900 font-medium dark:bg-gray-800 dark:border-gray-700 dark:text-gray-100"
            : "bg-transparent border border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200",
        ],
        variant === "underline" && [
          "-mb-[2px]",
          isActive
            ? "border-b-2 border-[var(--brand-accent-border-strong)] text-[var(--brand-primary)] font-semibold"
            : "border-b-2 border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200",
        ],
        className
      )}
      {...props}
    >
      {children}
    </button>
  );
}

export function TabPanel({ value: panelValue, className, children, ...props }: TabPanelProps) {
  const { value, tabsId } = useTabsContext();
  const seg = toIdSegment(panelValue);

  if (value !== panelValue) return null;

  return (
    <div
      id={`tabpanel-${tabsId}-${seg}`}
      role="tabpanel"
      aria-labelledby={`tab-${tabsId}-${seg}`}
      className={clsx("mt-4", className)}
      {...props}
    >
      {children}
    </div>
  );
}
